import { Router, Request, Response } from 'express'
import { AuthCore } from '../core'
import { asyncHandler } from '../frameworks/error-handler'
import { ValidationError, AuthenticationError } from '../errors'

/**
 * Create HTTP API routes for polyglot authentication
 */
export function createApiRoutes(auth: AuthCore): Router {
  const router = Router()

  // ============ Health & Info ============

  /**
   * GET /health
   * Health check endpoint
   */
  router.get('/health', (req: Request, res: Response) => {
    res.json({
      status: 'ok',
      timestamp: new Date().toISOString(),
      version: '1.0.0'
    })
  })

  /**
   * GET /info
   * Service information
   */
  router.get('/info', (req: Request, res: Response) => {
    res.json({
      name: '@kitium/auth',
      version: '1.0.0',
      description: 'Complete authentication solution',
      endpoints: {
        health: 'GET /health',
        info: 'GET /info',
        sessions: {
          get: 'GET /v1/sessions/:id',
          create: 'POST /v1/sessions',
          refresh: 'POST /v1/sessions/:id/refresh',
          delete: 'DELETE /v1/sessions/:id'
        },
        apiKeys: {
          create: 'POST /v1/keys',
          introspect: 'POST /v1/keys/introspect',
          list: 'GET /v1/keys',
          revoke: 'DELETE /v1/keys/:id'
        },
        users: {
          get: 'GET /v1/users/:id',
          create: 'POST /v1/users',
          update: 'PATCH /v1/users/:id',
          delete: 'DELETE /v1/users/:id'
        },
        oauth: {
          authorize: 'GET /oauth/authorize',
          callback: 'GET /oauth/callback/:provider',
          providers: 'GET /oauth/providers'
        }
      }
    })
  })

  // ============ Sessions ============

  /**
   * GET /v1/sessions/:id
   * Get session by ID
   */
  router.get(
    '/v1/sessions/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('Session ID is required')
      }

      const session = await auth.getSession(id)

      res.json({
        session,
        expiresIn: session ? Math.floor((session.expiresAt.getTime() - Date.now()) / 1000) : 0
      })
    })
  )

  /**
   * POST /v1/sessions
   * Create new session
   */
  router.post(
    '/v1/sessions',
    asyncHandler(async (req: Request, res: Response) => {
      const { userId, orgId } = req.body

      if (!userId || typeof userId !== 'string') {
        throw new ValidationError('User ID must be a non-empty string')
      }

      const session = await auth.createSession(userId, orgId)

      res.status(201).json({
        session,
        expiresIn: Math.floor((session.expiresAt.getTime() - Date.now()) / 1000)
      })
    })
  )

  /**
   * POST /v1/sessions/:id/refresh
   * Refresh session
   */
  router.post(
    '/v1/sessions/:id/refresh',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('Session ID is required')
      }

      const session = await auth.refreshSession(id)

      res.json({
        session,
        expiresIn: session ? Math.floor((session.expiresAt.getTime() - Date.now()) / 1000) : 0
      })
    })
  )

  /**
   * DELETE /v1/sessions/:id
   * Delete session
   */
  router.delete(
    '/v1/sessions/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('Session ID is required')
      }

      await auth.deleteSession(id)

      res.json({ message: 'Session deleted' })
    })
  )

  // ============ API Keys ============

  /**
   * POST /v1/keys
   * Create API key
   */
  router.post(
    '/v1/keys',
    asyncHandler(async (req: Request, res: Response) => {
      const { principalId, scopes, name, expiresAt, metadata } = req.body

      if (!principalId || typeof principalId !== 'string') {
        throw new ValidationError('Principal ID must be a non-empty string')
      }

      if (!scopes || !Array.isArray(scopes)) {
        throw new ValidationError('Scopes must be a non-empty array')
      }

      const apiKey = await auth.issueApiKey({
        principalId,
        scopes,
        name,
        expiresAt: expiresAt ? new Date(expiresAt) : undefined,
        metadata
      })

      res.status(201).json({
        ...apiKey,
        warning: 'This key will only be shown once. Store it securely.'
      })
    })
  )

  /**
   * POST /v1/keys/introspect
   * Introspect API key
   */
  router.post(
    '/v1/keys/introspect',
    asyncHandler(async (req: Request, res: Response) => {
      const { key } = req.body

      if (!key || typeof key !== 'string') {
        throw new ValidationError('API key must be a non-empty string')
      }

      const result = await auth.verifyApiKey(key)

      if (!result.valid) {
        return res.status(401).json({
          valid: false,
          error: 'Invalid API key'
        })
      }

      res.json(result)
    })
  )

  /**
   * GET /v1/keys
   * List API keys for principal
   */
  router.get(
    '/v1/keys',
    asyncHandler(async (req: Request, res: Response) => {
      const { principalId } = req.query

      if (!principalId || typeof principalId !== 'string') {
        throw new ValidationError('Principal ID query parameter is required')
      }

      const keys = await auth.listApiKeys(principalId)

      // Mask the keys for security
      const maskedKeys = keys.map(k => ({
        id: k.id,
        prefix: k.prefix,
        lastFour: k.lastFour,
        scopes: k.scopes,
        expiresAt: k.expiresAt,
        createdAt: k.createdAt
      }))

      res.json({ keys: maskedKeys })
    })
  )

  /**
   * DELETE /v1/keys/:id
   * Revoke API key
   */
  router.delete(
    '/v1/keys/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('API key ID is required')
      }

      await auth.revokeApiKey(id)

      res.json({ message: 'API key revoked' })
    })
  )

  // ============ Users ============

  /**
   * GET /v1/users/:id
   * Get user by ID
   */
  router.get(
    '/v1/users/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('User ID is required')
      }

      const user = await auth.getUser(id)

      res.json({ user })
    })
  )

  /**
   * POST /v1/users
   * Create user
   */
  router.post(
    '/v1/users',
    asyncHandler(async (req: Request, res: Response) => {
      const { email, name, picture, plan, entitlements, metadata } = req.body

      const user = await auth.createUser({
        email,
        name,
        picture,
        plan,
        entitlements,
        metadata
      })

      res.status(201).json({ user })
    })
  )

  /**
   * PATCH /v1/users/:id
   * Update user
   */
  router.patch(
    '/v1/users/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params
      const { email, name, picture, plan, entitlements, metadata } = req.body

      if (!id) {
        throw new ValidationError('User ID is required')
      }

      const user = await auth.updateUser(id, {
        email,
        name,
        picture,
        plan,
        entitlements,
        metadata
      })

      res.json({ user })
    })
  )

  /**
   * DELETE /v1/users/:id
   * Delete user
   */
  router.delete(
    '/v1/users/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('User ID is required')
      }

      await auth.deleteUser(id)

      res.json({ message: 'User deleted' })
    })
  )

  // ============ Entitlements ============

  /**
   * GET /v1/entitlements/:principalId
   * Get entitlements for principal
   */
  router.get(
    '/v1/entitlements/:principalId',
    asyncHandler(async (req: Request, res: Response) => {
      const { principalId } = req.params

      if (!principalId) {
        throw new ValidationError('Principal ID is required')
      }

      const entitlements = await auth.resolveEntitlements(principalId)

      res.json(entitlements)
    })
  )

  /**
   * POST /v1/entitlements/:principalId/check
   * Check if principal has entitlement
   */
  router.post(
    '/v1/entitlements/:principalId/check',
    asyncHandler(async (req: Request, res: Response) => {
      const { principalId } = req.params
      const { scope } = req.body

      if (!principalId) {
        throw new ValidationError('Principal ID is required')
      }

      if (!scope || typeof scope !== 'string') {
        throw new ValidationError('Scope must be a non-empty string')
      }

      const hasEntitlement = await auth.hasEntitlement(principalId, scope)

      res.json({ principalId, scope, hasEntitlement })
    })
  )

  // ============ Plans ============

  /**
   * GET /v1/plans
   * List all plans
   */
  router.get(
    '/v1/plans',
    asyncHandler(async (req: Request, res: Response) => {
      const plans = await auth.listPlans()

      res.json({ plans })
    })
  )

  /**
   * GET /v1/plans/:id
   * Get plan by ID
   */
  router.get(
    '/v1/plans/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('Plan ID is required')
      }

      const plan = await auth.getPlan(id)

      if (!plan) {
        return res.status(404).json({
          error: 'PLAN_NOT_FOUND',
          message: `Plan ${id} not found`
        })
      }

      res.json({ plan })
    })
  )

  // ============ Organizations ============

  /**
   * GET /v1/organizations/:id
   * Get organization by ID
   */
  router.get(
    '/v1/organizations/:id',
    asyncHandler(async (req: Request, res: Response) => {
      const { id } = req.params

      if (!id) {
        throw new ValidationError('Organization ID is required')
      }

      const org = await auth.getOrganization(id)

      res.json({ org })
    })
  )

  /**
   * POST /v1/organizations
   * Create organization
   */
  router.post(
    '/v1/organizations',
    asyncHandler(async (req: Request, res: Response) => {
      const { name, plan, ownerId, seats, metadata } = req.body

      if (!name || typeof name !== 'string') {
        throw new ValidationError('Organization name is required')
      }

      if (!ownerId || typeof ownerId !== 'string') {
        throw new ValidationError('Owner ID is required')
      }

      const org = await auth.createOrganization({
        name,
        plan: plan || 'free',
        ownerId,
        seats,
        metadata
      })

      res.status(201).json({ org })
    })
  )

  return router
}
